import { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks';
import { FiasSearchProps } from './types';
import { TypeChanger, Search, SelectedItems } from './components';
import { Wrapper, SearchWrapper } from './fias-search.styled';

/**
 * - `flat` (boolean): Определяет тип объектов в правом подменю (дома или квартиры).
 *    - true: Показываются квартиры.
 *    - false: Показываются дома.
 * - `selectOnlyHouses` (boolean): Ограничивает выбор только домами.
 *    - true: Можно выбирать только дома (улицы/города/поселки будут недоступны).
 *    - false: Можно выбирать улицы, поселки, города и дома.
 * - `withoutClearingOnUnmount` (boolean, optional): Определяет, будет ли состояние поиска очищаться при размонтировании компонента.
 * - `isDisabled` (boolean, optional): Позволяет отключить взаимодействие с компонентом (например, для блокировки интерфейса).
 * - `withSectors` (boolean, optional): Включает отображение секторов, если они доступны.
 *
 * Выходной результат сохраняется в три массива:
 * - `fias.selectedGroups`: выбранные группы домов.
 * - `fias.selectedFias`: выбранные улицы, поселки, города и другие объекты.
 * - `fias.selectedHouses`: выбранные дома.
 *
 * Каждый элемент в этих массивах имеет уникальный идентификатор (id для групп, AOGUID для объектов ФИАС, _id для домов)
 * и дополнительные данные, такие как name, parent (родительский объект), flats (список квартир при активной опции "квартиры") и flatsRange (диапазон квартир).
 * В зависимости от контекста, также могут присутствовать поля inheritors или houses.
 */
export const FiasSearch: FC<
  Pick<FiasSearchProps, 'selectOnlyHouses' | 'flat'> &
    Partial<Omit<FiasSearchProps, 'selectOnlyHouses' | 'flat'>> & {
      withoutHouseGroups?: boolean;
    }
> = observer(
  ({
    flat,
    selectOnlyHouses,
    withoutClearingOnUnmount = false,
    withSectors = false,
    isDisabled = false,
    disabledHouses = [],
    withoutHouseGroups = false,
  }) => {
    const { fias } = useStore();
    const [focus, setFocus] = useState<boolean>(false);
    const [leftMenuOpen, setLeftMenuOpen] = useState<boolean>(false);
    const [rightMenuOpen, setRightMenuOpen] = useState<boolean>(false);

    useEffect(() => {
      if (withSectors) {
        // Запрос древа фиаса в случае его отсутствия (с секторами)
        if (
          !fias.statuses.fiasTreeWithSectors.pending &&
          !fias.statuses.fiasTreeWithSectors.fulfilled
        ) {
          fias.getTreeWithSectors();
        }

        // Запрос групп в случае их отсутствия (с секторами)
        if (
          !fias.statuses.houseGroupsWithSectors.pending &&
          !fias.statuses.houseGroupsWithSectors.fulfilled
        ) {
          fias.getHouseGroupsWithSectors();
        }
      } else {
        // Запрос древа фиаса в случае его отсутствия (без секторов)
        if (fias.statuses.fiasTree.readyToLoad) {
          fias.getTree();
        }

        // Запрос групп в случае их отсутствия (без секторов)
        if (fias.statuses.houseGroups.readyToLoad) {
          fias.getHouseGroups();
        }
      }

      if (!withoutClearingOnUnmount) {
        // Очистка при анмаунте компонента
        return () => {
          fias.clearFiasSearch();
        };
      }
    }, []);

    // Во избежание одновременного раскрытия разных меню
    useEffect(() => {
      if (rightMenuOpen || leftMenuOpen) {
        setFocus(true);

        if (leftMenuOpen) {
          setRightMenuOpen(false);
        } else {
          setLeftMenuOpen(false);
        }
      } else {
        setFocus(false);
      }
    }, [leftMenuOpen, rightMenuOpen]);

    return (
      <Wrapper isDisabled={isDisabled}>
        <SearchWrapper isActive={focus} isDisabled={isDisabled}>
          <TypeChanger
            menuOpen={leftMenuOpen}
            setMenuOpen={setLeftMenuOpen}
            isDisabled={isDisabled}
            withoutHouseGroups={withoutHouseGroups}
          />
          <Search
            menuOpen={rightMenuOpen}
            setMenuOpen={setRightMenuOpen}
            flat={flat}
            selectOnlyHouses={selectOnlyHouses}
            withSectors={withSectors}
            isDisabled={isDisabled}
            disabledHouses={disabledHouses}
          />
        </SearchWrapper>
        <SelectedItems
          flat={flat}
          withSectors={withSectors}
          isDisabled={isDisabled}
          disabledHouses={disabledHouses}
        />
      </Wrapper>
    );
  }
);
